import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import {
  fontsUsedInDeck,
  googleFontsHref,
  renderSlideHtml,
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  type Deck,
  type Slide,
  type TrainingDesign,
} from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';

/**
 * Read-only slide, rendered from the same renderSlideHtml the PDF export uses and scaled down
 * from the SLIDE_WIDTH x SLIDE_HEIGHT canvas to `width`. The shared renderer escapes all text and
 * only ever shows diagrams through <img> data URIs; DOMPurify runs over the result anyway, as a
 * second line of defence for content Gemini wrote.
 */
export function SlideView({
  slide,
  design,
  imageUrls,
  width,
  slideNumber,
  animate,
  className,
}: {
  slide: Slide;
  design: TrainingDesign;
  imageUrls: Record<string, string>;
  width: number;
  /** The slide's 1-based position in its deck, shown on slides that have showNumber. */
  slideNumber?: number;
  /** Play the staggered entrance (pulse-animate in globals.css) when the slide mounts. */
  animate?: boolean;
  className?: string;
}) {
  const scale = width / SLIDE_WIDTH;
  const html = useMemo(
    () =>
      DOMPurify.sanitize(
        renderSlideHtml(slide, design, {
          resolveImage: (path) => imageUrls[path] ?? null,
          slideNumber,
        }),
      ),
    [slide, design, imageUrls, slideNumber],
  );
  return (
    <div
      className={`relative overflow-hidden ${animate ? 'pulse-animate' : ''} ${className ?? ''}`}
      style={{ width, height: SLIDE_HEIGHT * scale }}
    >
      <div
        style={{
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

/** Loads a deck's Google Fonts into the page once, so slides render in their real fonts. */
export function useDeckFonts(decks: (Deck | null | undefined)[]) {
  const fonts = useMemo(
    () => [...new Set(decks.flatMap((d) => (d ? fontsUsedInDeck(d) : [])))],
    [decks],
  );
  useGoogleFonts(fonts);
}

/** Loads Google Fonts into the page, once per distinct set. */
export function useGoogleFonts(fonts: string[]) {
  const key = [...fonts].sort().join('|');
  const href = useMemo(() => (key ? googleFontsHref(key.split('|')) : null), [key]);
  useEffect(() => {
    if (!href || document.head.querySelector(`link[data-deck-fonts="${CSS.escape(href)}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.deckFonts = href;
    document.head.appendChild(link);
  }, [href]);
}

/** Every uploaded image path a deck references. */
export function deckImagePaths(deck: Deck | null | undefined): string[] {
  if (!deck) return [];
  const paths = new Set<string>();
  for (const slide of deck.slides)
    for (const el of slide.elements)
      if (el.type === 'image' && el.storagePath) paths.add(el.storagePath);
  return [...paths].sort();
}

/** Signed read URLs for a deck's uploaded images, fetched whenever a new path shows up. URLs are
 * kept for the life of the component (they're signed for an hour). */
export function useSlideImageUrls(projectId: string, paths: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const missing = paths.filter((p) => !urls[p]);
  const key = missing.join('|');
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    api
      .post<{ urls: Record<string, string> }>(`/projects/${projectId}/training/images/urls`, {
        paths: key.split('|'),
      })
      .then((res) => {
        if (!cancelled) setUrls((u) => ({ ...u, ...res.urls }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, key]);
  return urls;
}

/** Uploads one image for the slide editor and returns its storage path. */
export async function uploadSlideImage(projectId: string, file: File): Promise<string> {
  const { uploadUrl, storagePath, contentType } = await api.post<{
    uploadUrl: string;
    storagePath: string;
    contentType: string;
  }>(`/projects/${projectId}/training/images/upload-url`, { filename: file.name });
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) throw new Error(`Upload of ${file.name} failed (${res.status})`);
  return storagePath;
}
